import React, { forwardRef } from 'react'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import { cn } from '@/lib/utils/cn'

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  /** Optional label text */
  label?: string
}

/**
 * Design-system Switch — Radix-based toggle.
 *
 * - Primary-600 on-state
 * - Subtle drop-shadow on thumb
 * - 200ms transition
 * - WCAG: keyboard accessible, focus-visible ring
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, label, id, ...props }, ref) => (
    <div className="flex items-center gap-2">
      <SwitchPrimitive.Root
        ref={ref}
        id={id}
        className={cn(
          'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full',
          'border-2 border-transparent bg-neutral-200',
          'transition-colors duration-200',
          'focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:bg-primary-600',
          className,
        )}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none block h-5 w-5 rounded-full bg-white',
            'shadow-sm ring-0',
            'transition-transform duration-200',
            'data-[state=checked]:translate-x-5',
            'data-[state=unchecked]:translate-x-0',
          )}
        />
      </SwitchPrimitive.Root>

      {label && (
        <label htmlFor={id} className="cursor-pointer select-none text-sm text-neutral-700">
          {label}
        </label>
      )}
    </div>
  ),
)

Switch.displayName = 'Switch'
